import { IGameSystem } from '../../core/IGameSystem';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { gameLogger } from '../../../utils/GameLogger';
import { getAbilityData } from '../../../shared/content/generated/abilities.gen';
import { CLASS_RESOURCE } from '../../../config/ui/classResource.config';

/**
 * ComboPointSystem — client-side combo-point (assassin star) tracker.
 *
 * There is NO server-side combo state (checked 2026-06-11: the player
 * schema has nothing; the only 'combo' hit in src/server is a feature-flag
 * description string), so this is client-owned presentation state, like
 * CooldownStore. Damage stays server-authoritative; stars are feedback.
 *
 * Rules (config-driven via classResource.config.ts):
 *  - +1 point (cap maxPoints) on a SUCCESSFUL local cast of an ability
 *    whose TOML tags intersect builderTags ('combo_point'/'combo_gen'/
 *    'combo_builder'). Hook: PlayerManager emits PLAYER_ACTION_INTENT
 *    {action:'castAbility'} only after all cast gates pass + the ability
 *    fn executes (stage-5 success path).
 *  - Spent to 0 by abilities tagged with spenderTags ('finisher').
 *  - Reset on TARGET_CHANGED (WoW-style per-target points; covers target
 *    death too — TargetingSystem clears the target on MOB_DIED, which
 *    re-emits TARGET_CHANGED null).
 *  - Reset after outOfCombatResetMs with no combo-relevant cast.
 *
 * Consumers:
 *  - PlayerRenderer reads the `comboPointState` module singleton per-frame
 *    (same React/canvas bridge pattern as netobs CastStripState) to float
 *    star pips above the local player.
 *  - PlayerInfoPanel (unit frame) subscribes to EVENTS.COMBO_POINTS_CHANGED.
 */

export interface ComboPointSnapshot {
  points: number;
  max: number;
  /** Runtime id of the LOCAL player (rebinds to the Colyseus session id
   *  after connect) — lets PlayerRenderer gate pips to the local sprite. */
  playerId: string | null;
}

/** Module singleton — writer: ComboPointSystem; readers: PlayerRenderer
 *  (per-frame) + PlayerInfoPanel (initial value before the first event). */
export const comboPointState = {
  points: 0,
  max: CLASS_RESOURCE.maxPoints,
  playerId: null as string | null,
  read(): ComboPointSnapshot {
    return { points: this.points, max: this.max, playerId: this.playerId };
  },
};

const PLAYER_LOOKUP_THROTTLE_MS = 250;

export class ComboPointSystem implements IGameSystem {
  public readonly id = 'ComboPointSystem';

  private eventBus = EventBus.getInstance();
  private unsubscribers: (() => void)[] = [];
  /** Date.now() of the last combo-relevant cast (builder OR spender) —
   *  the out-of-combat decay clock. */
  private lastActivityAt = 0;
  private lastPlayerLookupAt = -Infinity;

  public async initialize(): Promise<void> {
    this.unsubscribers.push(
      this.eventBus.on(EVENTS.PLAYER_ACTION_INTENT, (payload: { action?: string; data?: { ability_id?: string } }) => {
        if (payload?.action !== 'castAbility') return;
        this.onLocalCast(payload?.data?.ability_id);
      }),
      // Per-target points: new target / target cleared / target died → 0.
      this.eventBus.on(EVENTS.TARGET_CHANGED, () => this.reset('target_changed')),
    );
    gameLogger.debug('combat', '[ComboPointSystem] Initialized');
  }

  public update(_deltaTime: number, _time: number): void {
    const now = Date.now();

    // Keep the local player id fresh for the renderer (it rebinds to the
    // Colyseus session id after connect). Throttled — registry lookup only.
    if (now - this.lastPlayerLookupAt > PLAYER_LOOKUP_THROTTLE_MS) {
      this.lastPlayerLookupAt = now;
      comboPointState.playerId = this.getLocalPlayer()?.id ?? null;
    }

    // Out-of-combat decay.
    if (comboPointState.points > 0 && now - this.lastActivityAt > CLASS_RESOURCE.outOfCombatResetMs) {
      this.reset('out_of_combat');
    }
  }

  public dispose(): void {
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers = [];
    this.setPoints(0);
    comboPointState.playerId = null;
  }

  public getPoints(): number {
    return comboPointState.points;
  }

  public getMax(): number {
    return comboPointState.max;
  }

  // --- internals ---

  private onLocalCast(abilityId?: string): void {
    if (!abilityId) return;

    const player = this.getLocalPlayer();
    if (!player || !CLASS_RESOURCE.classes.includes(player.class)) return;

    const tags = getAbilityData(abilityId)?.tags ?? [];
    if (tags.length === 0) return;
    if (tags.some(t => CLASS_RESOURCE.excludeTags.includes(t))) return;

    if (tags.some(t => CLASS_RESOURCE.spenderTags.includes(t))) {
      // Finisher — consume the pool. (No client gate on 0-point finishers:
      // the cast itself is server-validated; stars are presentation.)
      this.lastActivityAt = Date.now();
      if (comboPointState.points > 0) {
        this.setPoints(0);
        gameLogger.debug('combat', `[ComboPointSystem] ${abilityId} spent combo points`);
      }
      return;
    }

    if (tags.some(t => CLASS_RESOURCE.builderTags.includes(t))) {
      this.lastActivityAt = Date.now();
      this.setPoints(Math.min(comboPointState.points + 1, comboPointState.max));
    }
  }

  private reset(reason: string): void {
    if (comboPointState.points === 0) return;
    this.setPoints(0);
    gameLogger.debug('combat', `[ComboPointSystem] Reset (${reason})`);
  }

  private setPoints(points: number): void {
    if (comboPointState.points === points) return;
    comboPointState.points = points;
    this.eventBus.emit(EVENTS.COMBO_POINTS_CHANGED, { points, max: comboPointState.max });
  }

  private getLocalPlayer(): { id: string; class: string } | null {
    try {
      return ServiceRegistry.getInstance()
        .get('EntitySystem', { optional: true })
        ?.getPlayerManager?.()?.getPlayer?.() ?? null;
    } catch {
      return null;
    }
  }
}
